/**
 * CWR 2.2 — pure display helpers for the Book a Session catalog cards.
 * Date/time use the browser timezone (the client-tz convention, matching the
 * Home page's homeFormat.ts), NOT the event's stored timezone.
 */
import { LOCATION_KIND_LABEL } from '@telify/shared';
import type { HubServiceDto } from '@/api/types';
import { formatDollarAmount } from '@/utils/formatPrice';

const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

/** "Tue, Jun 17 · 6:00 PM" (browser tz). */
export function formatEventDateTime(iso: string): string {
  const d = new Date(iso);
  return `${WEEKDAY.format(d)}, ${MONTH_DAY.format(d)} · ${TIME.format(d)}`;
}

/** Modality chip label. */
export function modalityLabel(modality: HubServiceDto['modality']): string {
  return LOCATION_KIND_LABEL[modality];
}

/** Spots remaining are "urgent" (accent-hot) at or below this count (mockup: 3 = low, 8 = normal). */
export const LOW_SPOTS_THRESHOLD = 3;

export type SpotsVariant = 'normal' | 'low' | 'out';

export interface SpotsLabel {
  text: string;
  variant: SpotsVariant;
}

/** Event spots: 0 → "Sold out" (out); ≤3 → low; else normal. */
export function eventSpotsLabel(spotsRemaining: number): SpotsLabel {
  if (spotsRemaining <= 0) return { text: 'Sold out', variant: 'out' };
  const text = `${spotsRemaining} ${spotsRemaining === 1 ? 'spot' : 'spots'} left`;
  return { text, variant: spotsRemaining <= LOW_SPOTS_THRESHOLD ? 'low' : 'normal' };
}

export interface PriceParts {
  free: boolean;
  /** Render a small muted "from" prefix before the amount (FROM pricing). */
  fromPrefix: boolean;
  /** "$90" / "$125"; empty when free. */
  amount: string;
}

/** Service price from the 2.1 pricing summary: FREE → "Free"; FIXED → "$90"; FROM → "from $125". */
export function servicePriceParts(pricing: HubServiceDto['pricing']): PriceParts {
  if (pricing.kind === 'FREE' || pricing.amount == null) {
    return { free: true, fromPrefix: false, amount: '' };
  }
  return { free: false, fromPrefix: pricing.kind === 'FROM', amount: formatDollarAmount(pricing.amount) };
}

/** Event price (single value): null/≤0 → "Free"; else "$30". */
export function eventPriceParts(price: number | null): PriceParts {
  if (price == null || price <= 0) return { free: true, fromPrefix: false, amount: '' };
  return { free: false, fromPrefix: false, amount: formatDollarAmount(price) };
}
